// Task slide-over. Opens from any view via ?task=<id>, so a link opens it too.
// Autosaves; fields the person may not edit are shown read-only, not hidden.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { CommentBody, MentionTextarea, useMentions } from '@/components/ui/mentions';
import remarkGfm from 'remark-gfm';
import { Check, Link2, Plus, Trash2, X } from 'lucide-react';
import { ConfirmDialog, RESTORE_NOTE } from '@/components/ui/confirm';
import { api, errorMessage } from '@/lib/api';
import {
  PRIORITY_LABEL,
  STATUS_LABEL,
  STATUS_ORDER,
  isOverdue,
  useTaskActions,
  useTracker,
  type Priority,
  type Task,
  type TaskPatch,
  type TaskStatus,
  trackerKey,
} from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn, fmtDate, fmtDateTime, fmtRelative } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea } from '@/components/ui/field';
import { Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';
import { Avatar, PhaseChip } from './bits';
import { TaskAttachments } from './TaskAttachments';

interface TaskDetail extends Omit<Task, 'comments'> {
  description: string | null;
  checklist: Array<{ id: string; text: string; done: boolean }>;
  comments: Array<{
    id: string;
    body: string;
    createdAt: string;
    author: { id: string; name: string };
    aiRunId: string | null;
  }>;
  editable: 'all' | string[];
}

interface Activity {
  at: string;
  action: string;
  actor: string;
  changes: Array<{ field: string; from: unknown; to: unknown }>;
}

/** Open a task in the slide-over from anywhere. */
export function useOpenTask() {
  const [params, setParams] = useSearchParams();
  return useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params);
      if (id) next.set('task', id);
      else next.delete('task');
      setParams(next, { replace: false });
    },
    [params, setParams],
  );
}

export function TaskPanelHost({ program }: { program: Program }) {
  const [params] = useSearchParams();
  const id = params.get('task');
  return id ? <TaskPanel key={id} taskId={id} program={program} /> : null;
}

function TaskPanel({ taskId, program }: { taskId: string; program: Program }) {
  const open = useOpenTask();
  const close = () => open(null);
  const returnFocus = useRef<Element | null>(document.activeElement);
  const panelRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const tracker = useTracker(program.id);
  const { patch } = useTaskActions(program.id);
  const base = `/api/programs/${program.id}`;

  const q = useQuery({
    queryKey: ['task', program.id, taskId],
    queryFn: () => api.get<TaskDetail>(`${base}/tasks/${taskId}`),
  });

  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && !(e.target as HTMLElement).closest('input,textarea,select') && close();
    document.addEventListener('keydown', onKey);
    const el = returnFocus.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      (el as HTMLElement | null)?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = (p: TaskPatch, label?: string) =>
    patch.mutate(
      { id: taskId, patch: p, undoLabel: label, silent: !label },
      { onSettled: () => qc.invalidateQueries({ queryKey: ['task', program.id, taskId] }) },
    );

  const comment = useMutation({
    mutationFn: (body: string) => api.post(`${base}/tasks/${taskId}/comments`, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task', program.id, taskId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  const dep = useMutation({
    mutationFn: (v: { blockerId: string; remove?: boolean }) =>
      v.remove
        ? api.delete(`${base}/tasks/${taskId}/blocked-by/${v.blockerId}`)
        : api.post(`${base}/tasks/${taskId}/blocked-by`, { blockerId: v.blockerId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['task', program.id, taskId] });
      void qc.invalidateQueries({ queryKey: ['tracker', program.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const [deleting, setDeleting] = useState(false);
  const removeTask = useMutation({
    mutationFn: () => api.delete(`${base}/tasks/${taskId}`),
    onSuccess: () => {
      setDeleting(false);
      toast.ok('Task deleted.');
      void qc.invalidateQueries({ queryKey: trackerKey(program.id) });
      close();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const t = q.data;
  const data = tracker.data;
  const can = (field: string) => t?.editable === 'all' || (Array.isArray(t?.editable) && t!.editable.includes(field));
  const phase = data?.phases.find((p) => p.id === t?.phaseId);
  const taskTitle = (id: string) => data?.tasks.find((x) => x.id === id)?.title ?? 'Task';

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Task details">
      <div className="absolute inset-0 bg-black/20" onClick={close} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-[min(640px,100vw)] flex-col border-l border-border bg-surface shadow-2xl outline-none"
      >
        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          {phase && <PhaseChip number={phase.number} name={phase.name} />}
          <span className="truncate text-xs text-muted">{phase?.name}</span>
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Copy link to this task"
              onClick={() => {
                void navigator.clipboard.writeText(window.location.href);
                toast.ok('Link copied.');
              }}
            >
              <Link2 />
            </Button>
            {program.capabilities.includes('plan.manage') && t && (
              <Button variant="ghost" size="icon" aria-label="Delete task" onClick={() => setDeleting(true)}>
                <Trash2 />
              </Button>
            )}
            <Button variant="ghost" size="icon" aria-label="Close" onClick={close}>
              <X />
            </Button>
          </div>
        </div>
        <ConfirmDialog
          open={deleting}
          title="Delete this task?"
          description={RESTORE_NOTE}
          onConfirm={() => removeTask.mutate()}
          onClose={() => setDeleting(false)}
          loading={removeTask.isPending}
        />

        {!t || !data ? (
          q.error ? (
            <p className="p-6 text-sm text-muted">{errorMessage(q.error)}</p>
          ) : (
            <Spinner />
          )
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 pt-4">
              <TitleField value={t.title} editable={can('title')} onSave={(title) => save({ title })} />
              {t.meeting && (
                <p className="mt-1 text-xs text-muted">
                  Action item from{' '}
                  <RouterLink
                    to={`/p/${program.id}/meetings/${t.meeting.id}`}
                    className="text-brand-text hover:underline"
                  >
                    {t.meeting.title}
                  </RouterLink>
                </p>
              )}
              {t.editable !== 'all' && (
                <p className="mt-1 text-xs text-muted">
                  {Array.isArray(t.editable) && t.editable.length
                    ? 'You can update progress on this task. Dates, owner and priority are set by BlackWolf; comment to ask for a change.'
                    : 'Read only. Comments are welcome.'}
                </p>
              )}
            </div>

            <dl className="mt-4 grid grid-cols-[120px_1fr] items-center gap-x-4 gap-y-2.5 px-5 text-[13px]">
              <Row label="Status">
                <Select
                  aria-label="Status"
                  className="h-8 w-44 text-[13px]"
                  value={t.status}
                  disabled={!can('status')}
                  onChange={(e) =>
                    save(
                      { status: e.target.value as TaskStatus },
                      `Status set to ${STATUS_LABEL[e.target.value as TaskStatus]}.`,
                    )
                  }
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </Select>
              </Row>
              <Row label="Owner">
                {can('ownerId') ? (
                  <Select
                    aria-label="Owner"
                    className="h-8 w-56 text-[13px]"
                    value={t.ownerId ?? ''}
                    onChange={(e) => save({ ownerId: e.target.value || null }, 'Owner changed.')}
                  >
                    <option value="">Unassigned</option>
                    {data.members.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span className="flex items-center gap-2">
                    <Avatar name={data.members.find((m) => m.userId === t.ownerId)?.name} />
                    {data.members.find((m) => m.userId === t.ownerId)?.name ?? 'Unassigned'}
                  </span>
                )}
              </Row>
              <Row label="Dates">
                <div className="flex items-center gap-2">
                  <DateField
                    label="Start"
                    value={t.startDate}
                    editable={can('startDate')}
                    onSave={(v) => save({ startDate: v }, 'Start date changed.')}
                  />
                  <span className="text-subtle">to</span>
                  <DateField
                    label="Due"
                    value={t.dueDate}
                    editable={can('dueDate')}
                    onSave={(v) => save({ dueDate: v }, 'Due date changed.')}
                  />
                  {isOverdue(t, data.today) && <span className="text-xs font-medium text-bad">Overdue</span>}
                </div>
              </Row>
              <Row label="Progress">
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    aria-label="Percent complete"
                    disabled={!can('percent')}
                    defaultValue={t.percent}
                    onMouseUp={(e) => save({ percent: Number((e.target as HTMLInputElement).value) })}
                    onKeyUp={(e) => save({ percent: Number((e.target as HTMLInputElement).value) })}
                    className="w-44 accent-[var(--brand)]"
                  />
                  <span className="tabular text-muted">{t.percent}%</span>
                </div>
              </Row>
              <Row label="Priority">
                <Select
                  aria-label="Priority"
                  className="h-8 w-36 text-[13px]"
                  value={t.priority}
                  disabled={!can('priority')}
                  onChange={(e) => save({ priority: e.target.value as Priority }, 'Priority changed.')}
                >
                  {(Object.keys(PRIORITY_LABEL) as Priority[]).map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABEL[p]}
                    </option>
                  ))}
                </Select>
              </Row>
              <Row label="Phase">
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    aria-label="Phase"
                    className="h-8 w-64 text-[13px]"
                    value={t.phaseId}
                    disabled={!can('phaseId')}
                    onChange={(e) => save({ phaseId: e.target.value }, 'Moved to another phase.')}
                  >
                    {data.phases.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.number}. {p.name}
                      </option>
                    ))}
                  </Select>
                  {phase && phase.workstreams.length > 0 && (
                    <Select
                      aria-label="Workstream"
                      className="h-8 w-52 text-[13px]"
                      value={t.workstreamId ?? ''}
                      disabled={!can('workstreamId')}
                      onChange={(e) => save({ workstreamId: e.target.value || null }, 'Workstream changed.')}
                    >
                      <option value="">No workstream</option>
                      {phase.workstreams.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </div>
              </Row>
              <Row label="Deliverable">
                <Select
                  aria-label="Linked deliverable"
                  className="h-8 w-full max-w-sm text-[13px]"
                  value={t.deliverableId ?? ''}
                  disabled={!can('deliverableId')}
                  onChange={(e) => save({ deliverableId: e.target.value || null }, 'Linked deliverable changed.')}
                >
                  <option value="">None</option>
                  {data.deliverables
                    .filter((d) => d.phaseId === t.phaseId || d.id === t.deliverableId)
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                </Select>
              </Row>
            </dl>

            <Section title="Description">
              <MarkdownField
                value={t.description ?? ''}
                editable={can('description')}
                onSave={(description) => save({ description: description || null })}
              />
            </Section>

            <Section
              title={`Checklist${t.checklist.length ? ` (${t.checklist.filter((c) => c.done).length}/${t.checklist.length})` : ''}`}
            >
              <Checklist
                items={t.checklist}
                editable={can('checklist')}
                onChange={(checklist) => save({ checklist })}
              />
            </Section>

            <Section title="Dependencies">
              <div className="flex flex-col gap-1.5 text-[13px]">
                {t.blockedBy.map((id) => (
                  <DepRow
                    key={id}
                    label="Blocked by"
                    title={taskTitle(id)}
                    onOpen={() => open(id)}
                    onRemove={can('ownerId') ? () => dep.mutate({ blockerId: id, remove: true }) : undefined}
                  />
                ))}
                {t.blocks.map((id) => (
                  <DepRow key={id} label="Blocks" title={taskTitle(id)} onOpen={() => open(id)} />
                ))}
                {!t.blockedBy.length && !t.blocks.length && <p className="text-muted">None.</p>}
                {t.editable === 'all' && (
                  <Select
                    aria-label="Add a task that blocks this one"
                    className="mt-1 h-8 max-w-sm text-[13px]"
                    value=""
                    onChange={(e) => e.target.value && dep.mutate({ blockerId: e.target.value })}
                  >
                    <option value="">Add a blocking task…</option>
                    {data.tasks
                      .filter((x) => x.id !== t.id && !t.blockedBy.includes(x.id))
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          P{data.phases.find((p) => p.id === x.phaseId)?.number} · {x.title}
                        </option>
                      ))}
                  </Select>
                )}
              </div>
            </Section>

            <Section title="Attachments">
              <TaskAttachments
                program={program}
                taskId={t.id}
                taskTitle={t.title}
                canUpload={can('description') || t.editable === 'all'}
              />
            </Section>

            <Section title="Comments">
              <div className="flex flex-col gap-3">
                {t.comments.map((c) => (
                  <div key={c.id} className="flex gap-2.5">
                    <Avatar name={c.author.name} size={24} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs">
                        <span className="font-medium">{c.author.name}</span>{' '}
                        <span className="text-subtle" title={fmtDateTime(c.createdAt)}>
                          {fmtRelative(c.createdAt)}
                        </span>
                      </p>
                      <div className="prose-helm mt-0.5 text-[13px]">
                        <CommentBody body={c.body} />
                      </div>
                    </div>
                  </div>
                ))}
                <CommentBox program={program} busy={comment.isPending} onSend={(b) => comment.mutate(b)} />
              </div>
            </Section>

            <Section title="History">
              <ActivityList programId={program.id} taskId={t.id} members={data.members} phases={data.phases} />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-5 border-t border-border px-5 py-4">
      <h3 className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">{title}</h3>
      {children}
    </section>
  );
}

function TitleField({ value, editable, onSave }: { value: string; editable: boolean; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!editable) return <h2 className="text-lg font-semibold leading-snug">{value}</h2>;
  return (
    <textarea
      aria-label="Title"
      rows={1}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => v.trim() && v !== value && onSave(v.trim())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
      className="w-full resize-none rounded-md border border-transparent bg-transparent px-1 py-0.5 text-lg font-semibold leading-snug hover:border-border focus:border-ring focus:outline-none"
    />
  );
}

function DateField({
  label,
  value,
  editable,
  onSave,
}: {
  label: string;
  value: string | null;
  editable: boolean;
  onSave: (v: string | null) => void;
}) {
  if (!editable)
    return <span className="tabular text-muted">{value ? fmtDate(value) : `No ${label.toLowerCase()} date`}</span>;
  return (
    <Input
      type="date"
      aria-label={`${label} date`}
      className="h-8 w-38 text-[13px]"
      defaultValue={value ?? ''}
      onBlur={(e) => (e.target.value || null) !== value && onSave(e.target.value || null)}
    />
  );
}

/** Edit in markdown, read rendered. Autosaves on blur. */
function MarkdownField({ value, editable, onSave }: { value: string; editable: boolean; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (editing && editable) {
    return (
      <Textarea
        autoFocus
        rows={8}
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          setEditing(false);
          if (v !== value) onSave(v);
        }}
        className="font-mono text-[12.5px]"
        placeholder="Markdown supported."
      />
    );
  }
  return (
    <div
      className={cn(
        'prose-helm min-h-10 rounded-md text-[13px]',
        editable && 'cursor-text px-2 py-1.5 -mx-2 hover:bg-surface-2',
      )}
      onClick={() => editable && setEditing(true)}
    >
      {value ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
      ) : (
        <p className="text-subtle">{editable ? 'Add a description…' : 'No description.'}</p>
      )}
    </div>
  );
}

function Checklist({
  items,
  editable,
  onChange,
}: {
  items: Array<{ id: string; text: string; done: boolean }>;
  editable: boolean;
  onChange: (items: Array<{ id: string; text: string; done: boolean }>) => void;
}) {
  const [text, setText] = useState('');
  return (
    <div className="flex flex-col gap-1">
      {items.map((c) => (
        <label key={c.id} className="group flex items-center gap-2 rounded px-1 py-0.5 text-[13px] hover:bg-surface-2">
          <input
            type="checkbox"
            className="size-4 accent-[var(--brand)]"
            checked={c.done}
            disabled={!editable}
            onChange={() => onChange(items.map((x) => (x.id === c.id ? { ...x, done: !x.done } : x)))}
          />
          <span className={cn('flex-1', c.done && 'text-subtle line-through')}>{c.text}</span>
          {editable && (
            <button
              type="button"
              aria-label={`Remove ${c.text}`}
              className="opacity-0 group-hover:opacity-100"
              onClick={(e) => {
                e.preventDefault();
                onChange(items.filter((x) => x.id !== c.id));
              }}
            >
              <Trash2 className="size-3.5 text-subtle" />
            </button>
          )}
        </label>
      ))}
      {editable && (
        <form
          className="mt-1 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!text.trim()) return;
            onChange([...items, { id: Math.random().toString(36).slice(2, 10), text: text.trim(), done: false }]);
            setText('');
          }}
        >
          <Input
            className="h-8 text-[13px]"
            placeholder="Add an item"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button type="submit" variant="secondary" size="sm" aria-label="Add checklist item">
            <Plus />
          </Button>
        </form>
      )}
      {!editable && !items.length && <p className="text-[13px] text-muted">No checklist.</p>}
    </div>
  );
}

function DepRow({
  label,
  title,
  onOpen,
  onRemove,
}: {
  label: string;
  title: string;
  onOpen: () => void;
  onRemove?: () => void;
}) {
  return (
    <div className="group flex items-center gap-2">
      <span className="w-20 shrink-0 text-xs text-muted">{label}</span>
      <button className="truncate text-left hover:underline" onClick={onOpen}>
        {title}
      </button>
      {onRemove && (
        <button
          aria-label={`Remove dependency on ${title}`}
          className="opacity-0 group-hover:opacity-100"
          onClick={onRemove}
        >
          <X className="size-3.5 text-subtle" />
        </button>
      )}
    </div>
  );
}

function CommentBox({ program, busy, onSend }: { program: Program; busy: boolean; onSend: (body: string) => void }) {
  const m = useMentions();
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.text.trim()) return;
        onSend(m.body());
        m.reset();
      }}
    >
      <MentionTextarea
        program={program}
        m={m}
        rows={2}
        placeholder="Write a comment. Markdown supported."
        className="text-[13px]"
      />
      <div>
        <Button type="submit" size="sm" loading={busy} disabled={!m.text.trim()}>
          <Check /> Comment
        </Button>
      </div>
    </form>
  );
}

const FIELD_NAME: Record<string, string> = {
  title: 'title',
  description: 'description',
  status: 'status',
  priority: 'priority',
  ownerId: 'owner',
  startDate: 'start date',
  dueDate: 'due date',
  percent: 'progress',
  checklist: 'checklist',
  phaseId: 'phase',
  workstreamId: 'workstream',
  deliverableId: 'deliverable',
  rank: 'position',
  deletedAt: 'removed',
};

function ActivityList({
  programId,
  taskId,
  members,
  phases,
}: {
  programId: string;
  taskId: string;
  members: Array<{ userId: string; name: string }>;
  phases: Array<{ id: string; number: number }>;
}) {
  const q = useQuery({
    queryKey: ['task', programId, taskId, 'activity'],
    queryFn: () => api.get<Activity[]>(`/api/programs/${programId}/tasks/${taskId}/activity`),
  });
  if (!q.data) return <Spinner className="py-4" />;
  const show = (field: string, v: unknown) => {
    if (v === null || v === undefined || v === '') return 'empty';
    if (field === 'ownerId') return members.find((m) => m.userId === v)?.name ?? 'someone';
    if (field === 'phaseId') return `Phase ${phases.find((p) => p.id === v)?.number ?? '?'}`;
    if (field === 'status') return STATUS_LABEL[v as TaskStatus] ?? String(v);
    if (field === 'startDate' || field === 'dueDate') return fmtDate(String(v).slice(0, 10));
    if (field === 'percent') return `${v}%`;
    if (field === 'checklist' || field === 'description') return 'updated';
    return String(v);
  };
  return (
    <ol className="flex flex-col gap-1.5 text-xs text-muted">
      {[...q.data].reverse().map((a, i) => (
        <li key={i} className="flex gap-2">
          <span className="w-28 shrink-0 tabular text-subtle" title={fmtDateTime(a.at)}>
            {fmtRelative(a.at)}
          </span>
          <span>
            <span className="font-medium text-text">{a.actor}</span>{' '}
            {a.action === 'create'
              ? 'created the task'
              : a.action === 'soft_delete'
                ? 'removed the task'
                : a.changes
                    .filter((c) => c.field !== 'rank')
                    .map((c) =>
                      c.field === 'checklist' || c.field === 'description'
                        ? `updated the ${FIELD_NAME[c.field]}`
                        : `changed ${FIELD_NAME[c.field] ?? c.field} from ${show(c.field, c.from)} to ${show(c.field, c.to)}`,
                    )
                    .join('; ') || 'reordered the task'}
          </span>
        </li>
      ))}
    </ol>
  );
}
